import { useState } from 'react'
import { AtSign, Globe, Loader2, Plus, Trash2 } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { useToast } from '../../../context/ToastContext'
import { ConfirmDialog, PanelState, cardCls, errorMessage, inputCls, primaryBtn } from './shared'

const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/
const MAILBOX_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function validateMapping(kind, value) {
  if (!value) return kind === 'mailbox' ? 'Enter a mailbox address' : 'Enter a domain'
  if (kind === 'mailbox') return MAILBOX_RE.test(value) ? null : 'Enter a full email address, e.g. orders@example.nl'
  if (value.includes('@')) return 'Enter the domain only, e.g. example.nl'
  return DOMAIN_RE.test(value) ? null : 'Enter a valid domain, e.g. example.nl'
}

// Mailbox and sender-domain mappings that route new requests to this OpCo.
// `domains` is the org-wide list (GET /opco/domains); the parent owns loading.
export default function DomainsPanel({ opco, domains, loading, error, onReload }) {
  const toast = useToast()
  const [kind, setKind] = useState('domain')
  const [value, setValue] = useState('')
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [removeBusy, setRemoveBusy] = useState(false)

  const mine = (domains || []).filter((d) => String(d.opco_id) === String(opco.id))
  const normalized = value.trim().toLowerCase()
  const validation = validateMapping(kind, normalized)

  const add = async (e) => {
    e.preventDefault()
    setTouched(true)
    if (validation) return
    setSaving(true)
    try {
      await opcoApi.addDomain(opco.id, kind, normalized)
      toast.success('Mapping added', `${normalized} now routes to ${opco.name}`)
      setValue('')
      setTouched(false)
      await onReload()
    } catch (err) {
      toast.error('Could not add mapping', errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const confirmRemove = async () => {
    setRemoveBusy(true)
    try {
      await opcoApi.removeDomain(removing.id)
      toast.success('Mapping removed', `${removing.value} no longer routes to ${opco.name}`)
      setRemoving(null)
      await onReload()
    } catch (err) {
      toast.error('Could not remove mapping', errorMessage(err))
    } finally {
      setRemoveBusy(false)
    }
  }

  return (
    <section className={cardCls} aria-label="Domains and mailboxes">
      <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Domains &amp; mailboxes</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          New requests received at a mapped mailbox, or sent from a mapped domain, are placed in this OpCo.
        </p>
      </div>

      <form onSubmit={add} className="flex flex-wrap items-start gap-2 px-5 py-4" noValidate>
        <select
          aria-label="Mapping type"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 text-sm text-gray-700 dark:text-gray-200"
        >
          <option value="domain">Sender domain</option>
          <option value="mailbox">Mailbox</option>
        </select>
        <div className="min-w-[12rem] flex-1">
          <input
            aria-label={kind === 'mailbox' ? 'Mailbox address' : 'Domain'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === 'mailbox' ? 'orders@example.nl' : 'example.nl'}
            className={inputCls}
            aria-invalid={Boolean(touched && validation)}
          />
          {touched && validation && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validation}</p>}
        </div>
        <button type="submit" disabled={saving || opco.is_active === false} className={primaryBtn}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add
        </button>
      </form>

      <PanelState
        loading={loading}
        error={error}
        onRetry={onReload}
        empty={mine.length === 0}
        emptyText="No mappings. Requests reach this OpCo only when created in its workspace or by its templates."
      >
        <ul className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
          {mine.map((d) => {
            const Icon = d.kind === 'mailbox' ? AtSign : Globe
            return (
              <li key={d.id} className="flex items-center gap-3 px-5 py-2.5">
                <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="flex-1 truncate font-mono text-sm text-gray-800 dark:text-gray-200">{d.value}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{d.kind === 'mailbox' ? 'Mailbox' : 'Domain'}</span>
                <button
                  type="button"
                  onClick={() => setRemoving(d)}
                  aria-label={`Remove ${d.value}`}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            )
          })}
        </ul>
      </PanelState>

      {removing && (
        <ConfirmDialog
          title="Remove mapping"
          message={`Stop routing ${removing.value} to ${opco.name}? New requests from it will go to Unassigned unless another rule matches.`}
          confirmLabel="Remove"
          danger
          busy={removeBusy}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  )
}
